
















































import { Component, Prop } from 'vue-property-decorator';
import CourseCardBase from '@/components/atoms/CourseCardBase.vue';
import CourseType from '@/components/atoms/CourseType.vue';
import CourseStatus from '@/components/atoms/CourseStatus.vue';
import NewcrossButton from '@/components/atoms/NewcrossButton.vue';
import NewcrossLoader from '@/components/atoms/NewcrossLoader.vue';
import LearningObject from '@/components/molecules/LearningObject.vue';
import HTMLContainer from '@/components/atoms/HTMLContainer.vue';
import {
  DoceboCourseDetail,
  DoceboEnrolmentStatus,
  DoceboLearningObjectPlayDetail,
  DoceboLearningObjectTrackDetail,
  DoceboLoTrackResponseHTML,
  DoceboLoTrackVideo,
} from '@/lib/DoceboAPI';
import { DialogInstance } from '@/plugins/DialogPlugin';
import { HealthForceGoSkill } from '@/lib/HealthForceGoAPI';
import calculateExpiryStatus, {
  ExpiryStatus,
} from '@/lib/calculateExpiryStatus';
import HealthForceGoApp from '@/lib/HealthForceGoApp';

@Component({
  components: {
    CourseType,
    CourseStatus,
    NewcrossButton,
    NewcrossLoader,
    LearningObject,
    HTMLContainer,
  },
})
export default class CourseCardELearning extends CourseCardBase {
  @Prop() courseId!: string;

  @Prop() courseName!: string;

  @Prop() courseDescription!: string;

  @Prop() enrolmentStatus!: DoceboEnrolmentStatus | null;

  private get locked(): boolean {
    return this.enrolmentStatus === 'locked';
  }

  private get catalogueId(): number {
    return Number(this.$route.params.catalogueId);
  }

  private get learningPlanId(): string {
    return this.$route.params.learningPlanId;
  }

  private get courseDetail(): DoceboCourseDetail | null {
    return this.$store.state.course.courseDetail[this.courseId];
  }

  private get courseSkills(): HealthForceGoSkill[] {
    return this.$store.getters['skills/getSkillsForCourse'](this.courseId);
  }

  private get showRenewButton(): boolean {
    const expiryStatus = calculateExpiryStatus(this.courseSkills);
    return [
      ExpiryStatus.INCOMPLETE,
      ExpiryStatus.EXPIRING_SOON,
      ExpiryStatus.EXPIRED,
    ].includes(expiryStatus);
  }

  private onDetailsToggle(e: Event) {
    const el = e.target as HTMLDetailsElement;
    if (el.open) {
      this.getCourseDetail(this.courseId);
    }
  }

  private getCourseDetail(courseId: string): Promise<void> {
    return this.$store.dispatch('course/GET_COURSE_DETAIL', courseId, {
      root: true,
    });
  }

  private enrolOnCourse() {
    this.$store.dispatch('SET_LOADING_OVERLAY', 'Enrolling');
    this.$store
      .dispatch('enrolments/ENROL_COURSE', this.courseId, {
        root: true,
      })
      .then(
        () => this.getCourseDetail(this.courseId),
        (error) => {
          // Show error message returned from Docebo (Too many enrolments?)
          const dialog = this.$dialog.show({
            title: error.message[0],
            buttons: [
              {
                label: 'Ok',
                handler: () => dialog.dismiss(),
              },
            ],
          });
        }
      )
      .finally(() => this.$store.dispatch('SET_LOADING_OVERLAY', false));
  }

  private resetCourse() {
    const dialog = this.$dialog.show({
      title:
        'Are you sure you wish to renew this course? All previous course progress will be lost.',
      buttons: [
        {
          label: 'Cancel',
          handler: () => dialog.dismiss(),
        },
        {
          label: 'Renew',
          handler: () => {
            dialog.dismiss();
            this.$store.dispatch('SET_LOADING_OVERLAY', 'Renewing');
            this.$store
              .dispatch('enrolments/RESET_COURSE', this.courseId, {
                root: true,
              })
              .then(() => this.getCourseDetail(this.courseId))
              .then(() => this.$store.dispatch('SET_LOADING_OVERLAY', false));
          },
        },
      ],
    });
  }

  private onLearningObjectPressed(
    loId: string,
    loName: string,
    loType: string
  ) {
    let dialog: DialogInstance;
    switch (loType) {
      case 'tincan':
      case 'scormorg':
        this.launchLearningObject(loId, loName);
        break;
      case 'video':
        this.launchLearningObjectVideo(loId, loName);
        break;
      case 'file':
        this.launchLearningObjectFile(loId);
        break;
      case 'htmlpage':
        this.launchLearningObjectHTML(loId, loName);
        break;
      default:
        dialog = this.$dialog.show({
          title: 'This type of content is not supported in Newcross World.',
          buttons: [
            {
              label: 'Ok',
              handler: () => dialog.dismiss(),
            },
          ],
        });
    }
  }

  private launchLearningObject(loId: string, loName: string) {
    this.$store.dispatch('SET_LOADING_OVERLAY', 'Loading');
    this.$store
      .dispatch('course/GET_COURSE_LEARNING_OBJECT_DETAIL', loId, {
        root: true,
      })
      .then((loDetail: DoceboLearningObjectPlayDetail) => {
        this.$store.dispatch('SET_LOADING_OVERLAY', false);
        this.$externalPage.show({
          name: loName,
          url: `https:${loDetail.data.launch_params.url}`,
          onClose: () => this.reloadData(),
        });
      });
  }

  private launchLearningObjectVideo(loId: string, loName: string) {
    this.$store.dispatch('SET_LOADING_OVERLAY', 'Loading');
    this.$store
      .dispatch('course/GET_COURSE_LEARNING_OBJECT_DETAIL', loId, {
        root: true,
      })
      .then((loDetail: DoceboLearningObjectPlayDetail) => {
        this.$store.dispatch('SET_LOADING_OVERLAY', false);
        this.$externalPage.show({
          name: loName,
          video: `https:${loDetail.data.launch_params.mp4_url}#t=${loDetail.data.launch_params.bookmark}`,
          bookmark: loDetail.data.launch_params.bookmark,
          onClose: (data: DoceboLoTrackVideo) => {
            if (data) {
              this.$store.dispatch('SET_LOADING_OVERLAY', 'Loading');
              this.$store
                .dispatch(
                  'course/TRACK_LO',
                  {
                    loId,
                    data,
                  },
                  {
                    root: true,
                  }
                )
                .then(() => {
                  this.$store.dispatch('SET_LOADING_OVERLAY', false);
                  this.reloadData();
                });
            } else {
              this.reloadData();
            }
          },
        });
      });
  }

  private launchLearningObjectFile(loId: string) {
    this.$store.dispatch('SET_LOADING_OVERLAY', 'Loading');
    this.$store
      .dispatch('course/GET_COURSE_LEARNING_OBJECT_TRACK_DETAIL', loId, {
        root: true,
      })
      .then((loDetail: DoceboLearningObjectTrackDetail) => {
        this.$store.dispatch('SET_LOADING_OVERLAY', false);
        const dialog = this.$dialog.show({
          title:
            'This will open an external link outside of HealthForceGo, do you wish to continue?',
          buttons: [
            {
              label: 'Cancel',
              handler: () => {
                dialog.dismiss();
                this.reloadData();
              },
            },
            {
              label: 'Ok',
              handler: () => {
                HealthForceGoApp.openExternalURL(
                  `https:${loDetail.data.acknowledge.url}`
                );
                dialog.dismiss();
                this.reloadData();
              },
            },
          ],
        });
      });
  }

  private launchLearningObjectHTML(loId: string, loName: string) {
    this.$store.dispatch('SET_LOADING_OVERLAY', 'Loading');
    this.$store
      .dispatch(
        'course/TRACK_LO',
        { loId },
        {
          root: true,
        }
      )
      .then((trackResponse: DoceboLoTrackResponseHTML) => {
        this.$store.dispatch('SET_LOADING_OVERLAY', false);
        this.$externalPage.show({
          name: loName,
          html: trackResponse.data.acknowledge.contents,
          onClose: () => this.reloadData(),
        });
      });
  }

  private reloadData() {
    this.$store.dispatch('SET_LOADING_OVERLAY', 'Loading');
    Promise.all([
      // Update catalogue
      this.$store.dispatch(
        'enrolments/GET_ENROLMENT_STATUS_FOR_CATALOGUE',
        Number(this.catalogueId),
        {
          root: true,
        }
      ),
      // Update course detail
      this.getCourseDetail(this.courseId),
      // If inside a learning plan, update the learning plan detail
      ...(this.learningPlanId
        ? [
            this.$store.dispatch(
              'learningPlan/GET_LEARNING_PLAN_DETAIL',
              this.learningPlanId,
              {
                root: true,
              }
            ),
          ]
        : []),
      // Update skills
      this.$store.dispatch('skills/GET_SKILLS_DATA'),
    ]).finally(() => {
      this.$store.dispatch('SET_LOADING_OVERLAY', false);
    });
  }
}
